import React, { useCallback, useMemo } from 'react'
import { range } from 'lodash'
import { PAGE_SIZE, PAGINATION_CONFIG } from '../constants'
import doubleLeft from '../images/double-left-blue.svg'
import singleLeft from '../images/single-left-blue.svg'
import doubleRight from '../images/double-right-blue.svg'
import singleRight from '../images/single-right-blue.svg'

/**
 * Renders a navigation button component for the pagination controls.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onPageSelected - The callback function to handle page selection.
 * @param {number} props.pageNumber - The page number to display on the button.
 * @param {string} props.image - The URL of the image to display on the button.
 * @param {boolean} props.condition - The condition to determine whether to render the button.
 * @returns {React.ReactNode} The rendered navigation button component.
 */
const NavigationButton = ({ onPageSelected, pageNumber, image, condition }) => {
  return condition ? (
    <span onClick={onPageSelected(pageNumber)}>
      <img alt={`page ${pageNumber}`} className="arrow" src={image} />
    </span>
  ) : null;
};

/**
 * Pagination component displays the pagination controls for Kendra search results.
 *
 * @see https://github.com/aws-solutions/kendra-chatbot-ui/blob/main/src/containers/Kendra/pagination/Pagination.jsx
 * @param {Object} props - The component props
 * @param {number} props.currentPageNumber - The current page number
 * @param {Object} props.results - The results object containing the total number of results and result items
 * @param {number} props.results.TotalNumberOfResults - The total number of results
 * @param {string} props.queryText - The query text
 * @param {Function} props.onSubmit - The function to handle the submit event
 * @returns {React.ReactNode} - The pagination component.
 */
const Pagination = ({
  currentPageNumber, 
  results, 
  queryText, 
  onSubmit 
}) => {
  /**
   * Callback function to handle page selection.
   *
   * @param {number} selectedPageNumber - The selected page number.
   * @returns {Function} A function to handle the submit event.
   */
  const onPageSelected = useCallback(
    selectedPageNumber => () => {
      onSubmit(queryText, selectedPageNumber);
    },
    [onSubmit, queryText]
  );

  /**
   * Renders a page number element.
   *
   * @param {number} page - The page number to render.
   * @returns {React.ReactNode} The rendered page number element.
   */
  const renderNumber = page => {
    return (
      <span
        className={
          page === currentPageNumber
            ? 'bold-text page-number action-link'
            : 'page-number action-link'
        }
        key={page}
        onClick={onPageSelected(page)}
      >
        {page}
      </span>
    );
  };

  const totalPages = useMemo(() =>
      Math.ceil(results?.TotalNumberOfResults / PAGE_SIZE),
    [results?.TotalNumberOfResults]
  );

  const pagesToShow = useMemo(() =>
      range(
        Math.max(1, currentPageNumber - PAGINATION_CONFIG.PREVIOUS_PAGES),
        Math.min(totalPages, currentPageNumber + PAGINATION_CONFIG.NEXT_PAGES) + 1
      ),
    [currentPageNumber, totalPages]
  );

  const firstButton = (
    <NavigationButton
      onPageSelected={onPageSelected}
      pageNumber={1}
      image={doubleLeft}
      condition={currentPageNumber > PAGINATION_CONFIG.NEXT_PAGES}
    />
  );

  const lastButton = (
    <NavigationButton
      onPageSelected={onPageSelected}
      pageNumber={totalPages}
      image={doubleRight}
      condition={totalPages - currentPageNumber > PAGINATION_CONFIG.NEXT_PAGES}
    />
  );

  const prevButton = (
    <NavigationButton
      onPageSelected={onPageSelected}
      pageNumber={currentPageNumber - 1}
      image={singleLeft}
      condition={currentPageNumber !== 1}
    />
  );

  const nextButton = (
    <NavigationButton
      onPageSelected={onPageSelected}
      pageNumber={currentPageNumber + 1}
      image={singleRight}
      condition={currentPageNumber < totalPages}
    />
  );

  const forwardEllipsis =
    totalPages - currentPageNumber > PAGINATION_CONFIG.NEXT_PAGES && (
      <span className="page-ellipses small-right-margin">...</span>
    );
  const backwardEllipsis =
    currentPageNumber > PAGINATION_CONFIG.NEXT_PAGES && (
      <span className="page-ellipses small-right-margin">...</span>
    );

  return (
    <div className="pagination-section">
      {firstButton}
      {prevButton}
      {backwardEllipsis}
      {pagesToShow.map(renderNumber)}
      {forwardEllipsis}
      {nextButton}
      {lastButton}
    </div>
  );
};

export default Pagination;